import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardPageFrame from '../../dashboard/components/DashboardPageFrame.jsx';
import MasterCrudTable from '../../dashboard/components/MasterCrudTable.jsx';
import TemplateSelectionPopup from '../../dashboard/components/TemplateSelectionPopup.jsx';
import { MASTER_DEFINITIONS, MASTER_GROUPS, USER_DEFINITIONS, getMasterDefinition } from '../../dashboard/constants/masterDefinitions.js';
import { generateFromTemplate, getMasterItems } from '../../dashboard/api/dashboardApi.js';
import '../../dashboard/styles/dashboard-master.css';

const FEATURE_TABS = [
  { label: 'Dashboard', href: '/dashboard/master' },
  { label: 'Life', href: '/meyasu/master' },
  { label: 'AWS利用料', href: '/aws-billing/master' },
  { label: 'システム管理', href: '/system/master' },
];

export default function SystemAdminPage() {
  const [allData, setAllData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeKey, setActiveKey] = useState(null);

  // Schedule template generation state
  const [selectedOemId, setSelectedOemId] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState(null);
  const [templatePopupOpen, setTemplatePopupOpen] = useState(false);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allDefs = [...MASTER_DEFINITIONS, ...USER_DEFINITIONS];
      const extraEndpoints = ['functions', 'permissions'];
      const results = await Promise.all([
        ...allDefs.map((def) => getMasterItems(def.endpoint)),
        ...extraEndpoints.map((ep) => getMasterItems(ep)),
      ]);
      const data = {};
      allDefs.forEach((def, i) => {
        data[def.key] = results[i];
      });
      extraEndpoints.forEach((ep, i) => {
        data[ep] = results[allDefs.length + i];
      });
      setAllData(data);
    } catch (loadError) {
      setError(loadError.message || 'マスタデータの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  // System admin: maxAssignableId is always 5
  const maxAssignableId = 5;
  const assignablePermissions = useMemo(
    () => (allData.permissions || []).filter((p) => p.id <= maxAssignableId),
    [allData.permissions],
  );

  // No permission_managements filtering — show all functions
  const supportingDataMap = useMemo(() => ({
    middle_categories: { largeCategories: allData.large_categories || [] },
    small_categories: { middleCategories: allData.middle_categories || [] },
    schedule_templates: {
      categories: allData.categories || [],
      smallCategories: allData.small_categories || [],
      actors: allData.actors || [],
      departments: allData.departments || [],
    },
    permission_managements: {
      users: allData.users || [],
      functions: allData.functions || [],
      permissions: assignablePermissions,
      // No defaultFunction — user selects function freely
    },
  }), [allData, assignablePermissions]);

  const oems = allData.oems || [];
  const regions = allData.regions || [];
  const templateDef = getMasterDefinition('schedule_templates');
  const templateItems = allData.schedule_templates || [];

  const handleReload = useCallback(async (masterKey) => {
    try {
      const def = MASTER_DEFINITIONS.find((d) => d.key === masterKey)
        || USER_DEFINITIONS.find((d) => d.key === masterKey);
      /* istanbul ignore next -- masterKey は必ず実在するマスタ定義のキーから渡るため到達不能な防御分岐 */
      if (!def) return;
      const items = await getMasterItems(def.endpoint);
      setAllData((prev) => ({ ...prev, [masterKey]: items }));
    } catch (_err) {
      // individual reload failure — ignore silently
    }
  }, []);

  const handleOpenTemplatePopup = () => {
    /* istanbul ignore next -- ボタンは OEM・対地未選択時に disabled のため到達不能な防御分岐 */
    if (!selectedOemId || !selectedRegionId) return;
    setGenerateResult(null);
    setTemplatePopupOpen(true);
  };

  const handleConfirmGenerate = async (templateIds, phaseMap = {}) => {
    setGenerating(true);
    setGenerateResult(null);
    try {
      const result = await generateFromTemplate({
        oem_id: Number(selectedOemId),
        region_id: Number(selectedRegionId),
        template_ids: templateIds,
        phase_map: phaseMap,
      });
      setGenerateResult(result);
      setTemplatePopupOpen(false);
    } catch (err) {
      setGenerateResult({ error: err.message || '登録に失敗しました。' });
    } finally {
      setGenerating(false);
    }
  };

  const activeDef = activeKey ? getMasterDefinition(activeKey) : null;

  // No filtering — show all permission_managements
  const getItemsForKey = (key) => {
    return allData[key] || [];
  };

  return (
    <DashboardPageFrame
      mainClassName="master-main"
      primaryTabActiveIndex={3}
      primaryTabs={FEATURE_TABS}
      rootClassName="master-page"
      sectionLabel=""
      stickyAreaClassName="master-sticky-area"
    >
      <section className="master-panel">
        {!loading && !error && (
          <div className="master-meta-card">
            <h3 className="master-meta-title">進捗スケジュール一括登録</h3>
            <p className="master-meta-description">
              OEM・対地を選択して、テンプレートから進捗スケジュールを一括登録します。
            </p>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap' }}>
              <label>
                OEM
                <select
                  value={selectedOemId}
                  onChange={(e) => setSelectedOemId(e.target.value)}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="">選択してください</option>
                  {oems.map((o) => (
                    <option key={o.id} value={o.id}>{o.oem}</option>
                  ))}
                </select>
              </label>
              <label>
                対地
                <select
                  value={selectedRegionId}
                  onChange={(e) => setSelectedRegionId(e.target.value)}
                  style={{ marginLeft: '8px' }}
                >
                  <option value="">選択してください</option>
                  {regions.map((r) => (
                    <option key={r.id} value={r.id}>{r.region}</option>
                  ))}
                </select>
              </label>
              <button
                className="master-primary-button"
                disabled={!selectedOemId || !selectedRegionId || generating}
                onClick={handleOpenTemplatePopup}
                type="button"
              >
                {generating ? '登録中...' : '一括登録'}
              </button>
            </div>
            {generateResult?.error && (
              <div className="master-alert master-alert--error" style={{ marginTop: '12px' }}>
                {generateResult.error}
              </div>
            )}
            {generateResult && !generateResult.error && (
              <div className={`master-alert ${generateResult.skipped ? 'master-alert--error' : 'master-alert--success'}`} style={{ marginTop: '12px' }}>
                {generateResult.message}
              </div>
            )}
          </div>
        )}

        <TemplateSelectionPopup
          loading={generating}
          onClose={() => setTemplatePopupOpen(false)}
          onConfirm={handleConfirmGenerate}
          open={templatePopupOpen}
          phases={allData.phases || []}
          templates={templateItems.map((item) => templateDef.fromApi(item))}
        />

        {loading && <div className="master-empty-state">読み込み中...</div>}
        {!loading && error && <div className="master-alert master-alert--error">{error}</div>}

        {/* All master groups — no isSuperAdmin filter */}
        {!loading && !error && (
          <div className="master-groups-grid">
            {MASTER_GROUPS.map((group) => (
              <div className={`master-group master-group--${group.colorClass}`} key={group.label}>
                <div className="master-group-header">
                  <h3 className="master-group-title">{group.label}</h3>
                </div>
                <div className="master-group-cards">
                  {group.keys.map((key) => {
                    const def = getMasterDefinition(key);
                    /* istanbul ignore next -- MASTER_GROUPS のキーは全て実在する定義のため到達不能な防御分岐 */
                    if (!def) return null;
                    const count = getItemsForKey(key).length;
                    return (
                      <button
                        className="master-table-card"
                        key={key}
                        onClick={() => setActiveKey(key)}
                        type="button"
                      >
                        <span className="master-table-card-label">{def.label}</span>
                        <span className="master-table-card-count">
                          {count}<span className="master-table-card-unit">件</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Table popup — readOnly is always false for system admin */}
        {activeDef && (
          <div className="ml-overlay" onClick={() => setActiveKey(null)}>
            <div className="master-table-popup" onClick={(e) => e.stopPropagation()}>
              <div className="master-table-popup-header">
                <h3 className="master-table-popup-title">{activeDef.label}</h3>
                <button
                  className="master-table-popup-close"
                  onClick={() => setActiveKey(null)}
                  type="button"
                >
                  ✕
                </button>
              </div>
              <div className="master-table-popup-body">
                <MasterCrudTable
                  definition={activeDef}
                  items={getItemsForKey(activeKey)}
                  onReload={() => handleReload(activeKey)}
                  readOnly={false}
                  supportingData={supportingDataMap[activeKey] || {}}
                />
              </div>
            </div>
          </div>
        )}
      </section>
    </DashboardPageFrame>
  );
}
